import React from 'react';
import { useLocation } from 'react-router-dom';
import { Avatar } from '@parsina/framework';
import { Coins, LogOut } from 'lucide-react';
import logo from 'url:../../assets/logo-white.png';

import { C, GRADIENT_SIDEBAR } from '../../constants/theme';
import { logout } from '../../lib/auth.js';
import { fmtParseh } from '../../constants/pricing';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useMe } from '../../hooks/useMe.js';
import { NAV_ITEMS, activeNavFor } from '../../constants/nav.js';
import AppConnectivityBanner from './AppConnectivityBanner.jsx';

/*
 * RTL layout: the rail is on the RIGHT of the screen.
 * When it collapses, `overflow: hidden` clips from the LEFT — so anything that
 * must stay visible (the toggle, the icons, the avatar) has to be anchored to
 * the RIGHT, i.e. come FIRST in RTL flex order. Every rule below follows from
 * that one sentence.
 */
const MINI = 68; // collapsed width
const FULL = 250; // expanded width

// The DESKTOP shell. App.jsx swaps it for MobileShell at ≤768px, so the collapse
// below is about a narrow laptop, not a phone: a 68px rail on a 360px viewport
// spends 19% of the width on chrome a thumb cannot reach.
//
// The destination list is src/constants/nav.js — ONE copy, shared with the mobile
// bottom bar, because two hand-kept lists is exactly how a bar and a rail drift
// apart. Nothing about a destination is declared here; this file only draws it.
export { activeNavFor };

const StudentLayout = ({ children }) => {
  const { t, formatNumber } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();
  const { me, balance } = useMe();

  const [collapsed, setCollapsed] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1100,
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 1100px)');
    const onChange = (e) => setCollapsed(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const current = activeNavFor(location.pathname);

  // Labels live in the DOM always and fade, so the width transition stays smooth.
  const fade = {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.22s ease',
    pointerEvents: collapsed ? 'none' : 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  return (
    <div style={{ display: 'flex', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {/* ── Rail (RIGHT in RTL) ── */}
      <div
        style={{
          width: collapsed ? MINI : FULL,
          flexShrink: 0,
          background: GRADIENT_SIDEBAR,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
          zIndex: 10,
        }}
      >
        {/* Header: toggle FIRST → rightmost → never clipped. */}
        <div
          style={{
            height: 68,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <div style={{ width: MINI, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? t('nav.sidebar.expand') : t('nav.sidebar.collapse')}
              aria-label={collapsed ? t('nav.sidebar.expand') : t('nav.sidebar.collapse')}
              style={{
                width: 36,
                height: 36,
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="18"
                height="18"
                style={{
                  transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
                  // The glyph points at the rail's own edge, so it mirrors with
                  // the layout as well as rotating with the toggle.
                  transform: `scaleX(var(--dir-flip, 1)) rotate(${collapsed ? 0 : 180}deg)`,
                }}
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              paddingInlineStart: 4,
              paddingInlineEnd: 8,
              ...fade,
            }}
          >
            <img
              src={logo}
              alt=""
              style={{ height: '2.2rem', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
            />
            <div>
              <div style={{ color: 'white', fontSize: 16, fontWeight: 800, whiteSpace: 'nowrap' }}>
                {t('nav.brand.name')}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, whiteSpace: 'nowrap' }}>
                {t('nav.brand.panel')}
              </div>
            </div>
          </div>
        </div>

        {/* ── Nav ── */}
        <nav
          aria-label={t('nav.landmark.primary')}
          style={{ flex: 1, paddingTop: 10, overflowY: 'auto', overflowX: 'hidden' }}
        >
          {NAV_ITEMS.map((item) => {
            const active = current === item.id;
            const { Icon } = item;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(item.id)}
                title={collapsed ? t(item.labelKey) : undefined}
                aria-current={active ? 'page' : undefined}
                style={{
                  width: 'calc(100% - 20px)',
                  margin: '2px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0,
                  border: 'none',
                  borderRadius: 10,
                  background: active ? C.navy : 'transparent',
                  boxShadow: active ? '0 2px 8px rgba(0,0,0,0.22)' : 'none',
                  cursor: 'pointer',
                  transition: 'background 0.18s, box-shadow 0.18s',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = 'transparent';
                }}
              >
                {/* Icon first in RTL → rightmost → never clipped. */}
                <span
                  style={{
                    width: MINI - 20,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '11px 0',
                    color: active ? 'white' : 'rgba(255,255,255,0.55)',
                  }}
                >
                  <Icon size={20} aria-hidden />
                </span>
                <span
                  style={{
                    ...fade,
                    color: active ? 'white' : 'rgba(255,255,255,0.55)',
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {t(item.labelKey)}
                </span>
              </button>
            );
          })}
        </nav>

        {/*
          ── The پارسه badge ──
          Teal, not the navy `Badge variant="count"` the kit uses for a tally on a
          light surface: this rail IS the navy gradient, and a navy chip on it is
          invisible. DESIGN.md states that exception in as many words — "one
          unread swatch per surface family", and teal is already this surface's
          established highlight.

          It is a BUTTON to شارژ پارسه, because the number is only ever read when
          the answer to it is "top up". A null balance renders «—» rather than a
          zero: the query is in flight, and printing 0 there would tell a student
          with plenty that they have nothing.
        */}
        <button
          type="button"
          onClick={() => navigate('credits')}
          title={t('nav.balance.label')}
          aria-label={t('nav.balance.label')}
          data-testid="rail-balance"
          style={{
            display: 'flex',
            alignItems: 'center',
            margin: '0 0 6px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontFamily: 'inherit',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: MINI,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              color: C.teal,
            }}
          >
            <Coins size={19} aria-hidden />
          </span>
          <span style={{ ...fade, display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>
              {t('nav.balance.label')}
            </span>
            <span style={{ color: C.teal, fontSize: 14, fontWeight: 800 }}>
              {balance == null ? t('common.labels.none') : fmtParseh(balance, formatNumber)}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>
              {t('common.units.parseh')}
            </span>
          </span>
        </button>

        {/* ── Student card ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 0',
            borderTop: '1px solid rgba(255,255,255,0.07)',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {/* aria-label, not just `title`: this control's only content is the
              kit's Avatar, whose text is the student's INITIALS — so without a
              label it announces as «م ا button». */}
          <div
            onClick={() => navigate('profile')}
            role="button"
            tabIndex={0}
            aria-label={t('nav.sidebar.editProfile')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('profile');
              }
            }}
            title={t('nav.sidebar.editProfile')}
            style={{ width: MINI, flexShrink: 0, display: 'flex', justifyContent: 'center', cursor: 'pointer' }}
          >
            <Avatar name={me?.fullName || ''} size={36} bg={C.navy} />
          </div>

          {/* Collapsed, this block is invisible (`fade` → opacity 0) and inert to
              the mouse (`pointerEvents: none`) — but neither of those removes a
              control from the TAB ORDER. Without the tabIndex below, the sign-out
              button is reachable by keyboard while painted at zero opacity:
              focus vanishes into a control the student cannot see, and Enter
              signs them out. The avatar above stays operable in both states, so
              nothing is lost. */}
          <div
            aria-hidden={collapsed || undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, overflow: 'hidden', ...fade }}
          >
            <div
              onClick={() => navigate('profile')}
              role="button"
              tabIndex={collapsed ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('profile');
                }
              }}
              style={{ flex: 1, overflow: 'hidden', cursor: 'pointer' }}
            >
              <div
                style={{
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {me?.fullName || t('common.labels.none')}
              </div>
              <div
                style={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: 11,
                  marginTop: 2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {t('nav.brand.panel')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              tabIndex={collapsed ? -1 : 0}
              title={t('nav.sidebar.logout')}
              aria-label={t('nav.sidebar.logout')}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'rgba(255,255,255,0.4)',
                padding: 4,
                borderRadius: 6,
                display: 'flex',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(255,255,255,0.4)';
              }}
            >
              {/* Not mirrored: the arrow points out of a door, not along the
                  reading direction. */}
              <LogOut size={16} aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {/* ── Page content (LEFT in RTL) ──
          A <main>, not a <div>: with the rail named above, the two landmarks are
          what a screen-reader user jumps between instead of tabbing past three
          nav rows on every page. */}
      <main
        aria-label={t('nav.landmark.content')}
        style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Once, above every routed screen: the bar pushes the page down rather
            than floating over its header (core.md §7.9). */}
        <AppConnectivityBanner pathname={location.pathname} />
        {children}
      </main>
    </div>
  );
};

export default StudentLayout;
